import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import ReactImageGallery from 'react-image-gallery';

import { propTypes } from '../../../util/types';
import { isRenderedImage } from '../../../util/renderedImageCore';
import { FormattedMessage, injectIntl, intlShape } from '../../../util/reactIntl';
// import { useHistory, useLocation } from 'react-router-dom';
// import { createResourceLocatorString } from '../../../util/routes';
// import { useRouteConfiguration } from '../../../context/routeConfigurationContext';
// import { useDispatch, useSelector } from 'react-redux';
// import { ensureUser } from '../../../util/data';
// import { updateFavourites } from '../../SearchPage/SearchPage.duck';
// import favouriteIconLiked from '../../../assets/favouriteIconLiked.svg';
// import favouriteIconNotLiked from '../../../assets/favouriteIconNotLiked.svg';
import {
  AspectRatioWrapper,
  Button,
  IconClose,
  IconArrowHead,
  ResponsiveImage,
} from '../../../components';

// Copied directly from
// `node_modules/react-image-gallery/styles/css/image-gallery.css`. The
// copied file is left unedited, and all the overrides are defined in
// the component CSS file below.
import './image-gallery.css';

import css from './ListingImageGallery.module.css';

const IMAGE_GALLERY_OPTIONS = {
  showPlayButton: false,
  disableThumbnailScroll: true,
};
const MAX_LANDSCAPE_ASPECT_RATIO = 2; // 2:1
const MAX_PORTRAIT_ASPECT_RATIO = 4 / 3;

const getFirstImageAspectRatio = (firstImage, scaledVariant) => {
  if (!firstImage) {
    return { aspectWidth: 1, aspectHeight: 1 };
  }

  const v = firstImage?.attributes?.variants?.[scaledVariant];
  const w = v?.width;
  const h = v?.height;
  const hasDimensions = !!w && !!h;
  const aspectRatio = w / h;

  // We keep the fractions separated as these are given to AspectRatioWrapper
  // which expects separate width and height
  return hasDimensions && aspectRatio >= MAX_LANDSCAPE_ASPECT_RATIO
    ? { aspectWidth: 2, aspectHeight: 1 }
    : hasDimensions && aspectRatio <= MAX_PORTRAIT_ASPECT_RATIO
      ? { aspectWidth: 4, aspectHeight: 3 }
      : hasDimensions
        ? { aspectWidth: w, aspectHeight: h }
        : { aspectWidth: 1, aspectHeight: 1 };
};

const ListingImageGallery = props => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const { intl, rootClassName, className, images, imageVariants,
    thumbnailVariants, extraSlide, currentUser, currentListing } = props;
  const thumbVariants = thumbnailVariants || imageVariants;
  const listingTitle = currentListing?.attributes?.title || '';
  const listingPublicData = currentListing?.attributes?.publicData;

  // const routeConfiguration = useRouteConfiguration();
  // const history = useHistory();
  // const location = useLocation();
  // const dispatch = useDispatch();

  // const { SearchPage: searchPageReducer } = useSelector(state => state);
  // const { updateFavouritesInProgress } = searchPageReducer || {};
  // const author = ensureUser(currentListing.author);

  // const [liked, setLiked] = useState(false);
  // const isOwnListing = author?.id?.uuid === currentUser?.id?.uuid;
  // const userFavouriteListings =
  //   (currentUser && currentUser.attributes.profile.publicData.favoriteListings) || [];

  // useEffect(() => {
  //   if (isLiked()) {
  //     setLiked(true);
  //   }
  // }, [userFavouriteListings]);

  // const isLiked = () => {
  //   const likedListings = userFavouriteListings;
  //   return likedListings && likedListings.includes(currentListing.id.uuid);
  // };

  // const toggleFavourite = event => {
  //   event.preventDefault();
  //  event.stopPropagation();

  //   if (!currentUser) {
  //     const state = { from: `${location.pathname}${location.search}${location.hash}` };
  //     history.push(createResourceLocatorString('SignupPage', routeConfiguration, {}, {}), state);
  //   } else {
  //     if (updateFavouritesInProgress) return;
  //     setLiked(!liked);

  //     const likedListings = userFavouriteListings;
  //     let newListings = likedListings ? likedListings : [];
  //     if (isLiked()) {
  //       newListings = likedListings.filter(l => l !== currentListing.id.uuid);
  //     } else {
  //       newListings.push(currentListing.id.uuid);
  //     }
  //     dispatch(updateFavourites(newListings));
  //   }
  // };

  // imageVariants are scaled variants.
  const { aspectWidth, aspectHeight } = getFirstImageAspectRatio(images?.[0], imageVariants[0]);
  let items = (images || []).map((img, i) => {
    return {
      // We will only use the image resource, but react-image-gallery
      // requires the `original` key from each item.
      original: '',
      alt: intl.formatMessage(
        { id: 'ListingImageGallery.imageAltText' },
        { listingTitle, index: i + 1, count: images.length }
      ),
      thumbAlt: intl.formatMessage(
        { id: 'ListingImageGallery.imageThumbnailAltText' },
        { listingTitle, index: i + 1, count: images.length }
      ),
      thumbnail: img.attributes?.variants?.[thumbVariants[0]],
      image: img,
    };
  });
  // Only weave in the custom slide when there is at least one real image —
  // otherwise items[0] is undefined and react-image-gallery crashes trying
  // to render an item with no `type`/`image`.
  if (extraSlide && items.length > 0) {
    items = [
      items[0], // first image
      {
        type: 'custom',
        id: 'avatar-bio-slide',
        content: extraSlide,
      },
      ...items.slice(1),
    ];
  }
  /* P25009-482 BR-10 — `sizes` must describe the box the hero is really drawn
   * in, or the browser downloads a variant smaller than the box and stretches
   * it. It used to say 708px up to the desktop layout, which on a 1440px
   * screen is drawn about 990px wide. The widths follow the page layout: the
   * gallery column is the page minus its gutters (20px / 32px), and from the
   * two-column breakpoint (1280px) also minus the 360px buy box and the 24px
   * gap, up to the 1464px content cap. */
  const imageSizesMaybe = isFullscreen
    ? {}
    : {
        sizes:
          '(max-width: 766px) calc(100vw - 40px), (max-width: 1279px) calc(100vw - 64px), ' +
          '(max-width: 1464px) calc(100vw - 448px), 1016px',
      };
  const renderItem = item => {
    if (item.type === 'custom') {
      return (
        <AspectRatioWrapper width={aspectWidth} height={aspectHeight} className={css.itemWrapper}>
          <div className={css.itemCentering}>{item.content}</div>
        </AspectRatioWrapper>
      );
    }
    /* P25009-448 BR-3 — a package's tyre-on-rim montage carries the
     * "Illustration" label as an overlay here, never inside the image file,
     * so the file stays clean for Google Shopping and Meta. It shows inline
     * and in the fullscreen view, which render through this same function. */
    if (isRenderedImage(listingPublicData, item.image)) {
      return (
        <div className={css.illustratedSlide}>
          <ResponsiveImage
            image={item.image}
            alt={item.alt}
            variants={imageVariants}
            {...imageSizesMaybe}
            rootClassName={isFullscreen ? null : css.heroImage}
            style={{ borderRadius: '10px' }}
          />
          <span className={css.illustrationBadge}>
            <FormattedMessage id="ListingImageGallery.illustrationBadge" />
          </span>
        </div>
      );
    }
    return (
      // <AspectRatioWrapper
      //   width={aspectWidth || 1}
      //   height={aspectHeight || 1}
      // className={isFullscreen ? css.itemWrapperFullscreen : css.itemWrapper}
      // >
      // <div className={css.itemCentering}>
      <ResponsiveImage
        // rootClassName={css.item}
        image={item.image}
        alt={item.alt}
        variants={imageVariants}
        {...imageSizesMaybe}
        rootClassName={isFullscreen ? null : css.heroImage}
        style={{ borderRadius: '10px' }}
      />
      // {!isOwnListing && (
      //     <div className={css.heartImageWrapper} onClick={toggleFavourite} >
      //       <div className={css.listingLike}>
      //         <div
      //           className={
      //             updateFavouritesInProgress ? classNames(css.disabled, css.heartIcon) : css.heartIcon
      //           }
      //         // onClick={toggleFavourite}
      //         >
      //           <img
      //             src={liked ? favouriteIconLiked : favouriteIconNotLiked}
      //             className={css.favouriteIcon}
      //           />
      //         </div>
      //       </div>
      //     </div>
      //   )}
      // </div>
      // </AspectRatioWrapper>
    );
  };
  const renderThumbInner = item => {
    return (
      <div>
        <ResponsiveImage
          rootClassName={css.thumb}
          image={item.image}
          alt={item.thumbAlt}
          variants={thumbVariants}
          sizes="88px"
        />
      </div>
    );
  };

  const onScreenChange = isFull => {
    setIsFullscreen(isFull);
  };

  const renderLeftNav = (onClick, disabled) => {
    return (
      <button className={css.navLeft} disabled={disabled} onClick={onClick}>
        <div className={css.navArrowWrapper}>
          <IconArrowHead direction="left" size="big" />
        </div>
      </button>
    );
  };
  const renderRightNav = (onClick, disabled) => {
    return (
      <button className={css.navRight} disabled={disabled} onClick={onClick}>
        <div className={css.navArrowWrapper}>
          <IconArrowHead direction="right" size="big" />
        </div>
      </button>
    );
  };
  const renderFullscreenButton = (onClick, isFullscreen) => {
    return isFullscreen ? (
      <Button
        onClick={onClick}
        rootClassName={css.close}
        title={intl.formatMessage({ id: 'ListingImageGallery.closeModalTitle' })}
      >
        <span className={css.closeText}>
          <FormattedMessage id="ListingImageGallery.closeModal" />
        </span>
        <IconClose rootClassName={css.closeIcon} />
      </Button>
    ) : (
      <button className={css.openFullscreen} onClick={onClick}>
        <FormattedMessage
          id="ListingImageGallery.viewImagesButton"
          values={{ count: images.length }}
        />
      </button>
    );
  };

  if (items.length === 0) {
    return <ResponsiveImage className={css.noImage} image={null} variants={[]} alt="" />;
  }

  const classes = classNames(rootClassName || css.root, className);

  return (
    <ReactImageGallery
      additionalClass={classes}
      items={items}
      renderItem={renderItem}
      renderThumbInner={renderThumbInner}
      onScreenChange={onScreenChange}
      renderLeftNav={renderLeftNav}
      renderRightNav={renderRightNav}
      renderFullscreenButton={renderFullscreenButton}
      {...IMAGE_GALLERY_OPTIONS}
    />
  );
};

ListingImageGallery.defaultProps = {
  rootClassName: null,
  className: null,
  thumbnailVariants: null,
};

const { string, arrayOf } = PropTypes;

ListingImageGallery.propTypes = {
  rootClassName: string,
  className: string,
  images: arrayOf(propTypes.image).isRequired,
  imageVariants: arrayOf(string).isRequired,
  thumbnailVariants: arrayOf(string),

  // from injectIntl
  intl: intlShape.isRequired,
};

export default injectIntl(ListingImageGallery);
